import {
  addSpaceToEndOfTranscriptElements,
  fillInWhitespacesInTranscriptElements,
} from '@src/editor/services/punctuation';
import { makeAutoObservable, observable } from 'mobx';

import type { KaraokeData, KaraokeLine } from '../editor/types/karaoke';
import type { IVideoEditor } from '@src/editor/interface/IVideoEditor';
import type { KaraokeConfig } from '@src/editor/models/karaoke/KaraokeConfig';
import type { KaraokeConfig as KaraokeConfigLegacy } from '@src/videoTranscriptionProcessor/types/karaokeTypes';

/**
 * KaraokeStore - Manages global karaoke-related state
 *
 * This store maintains karaoke state that is obtained from the VideoEditor instance.
 * It provides a centralized location for karaoke data that can be accessed across
 * the application.
 *
 * State:
 * - karaokeLines: Array of karaoke lines with transcript tokens
 * - isKaraokeGenerated: Whether karaoke has been generated
 * - isKaraokeLoading: Whether karaoke is currently being generated
 */
export class KaraokeStore {
  // Core state
  karaokeLines: KaraokeLine[] = [];
  loadedKaraokeData: KaraokeData | null = null;
  legacyKaraokeConfigLoaded: KaraokeConfigLegacy | null = null;
  karaokeConfigs: Map<string, KaraokeConfig> = new Map();
  defaultKaraokeConfig: KaraokeConfig | null = null;
  isKaraokeGenerated: boolean = false;
  isKaraokeLoading: boolean = false;
  karaokeLanguage: 'original' | 'english' | null = null;

  constructor() {
    makeAutoObservable(this, {
      loadedKaraokeData: false,
      karaokeLines: observable.shallow,
    });
  }

  /**
   * Sync karaoke state from VideoEditor instance
   * This should be called whenever the VideoEditor state changes
   *
   * @param editor - The VideoEditor instance to sync from
   */
  syncFromEditor(editor: IVideoEditor | null) {
    if (!editor) {
      this.reset();
      return;
    }
    // Get karaoke lines from editor
    const lines = editor.getKaraokeLines();
    this.setKaraokeLines(lines);
    this.karaokeConfigs = editor.getKaraokeConfigs();
    this.defaultKaraokeConfig = editor.getDefaultKaraokeConfig() ?? null;

    if (this.karaokeConfigs.size > 100) {
      console.warn('KaraokeStore: Excessive number of karaoke configs', {
        count: this.karaokeConfigs.size,
      });
    }

    // Get karaoke track to determine if karaoke is generated
    const karaokeTrack = editor.getKaraokeTrack();
    const hasKaraokeClips = karaokeTrack
      ? karaokeTrack.elements.length > 0
      : false;
    this.setIsKaraokeGenerated(hasKaraokeClips);
    console.debug('KaraokeStore: Synced from editor', {
      linesCount: lines.length,
      hasKaraokeClips,
      karaokeConfigs: Array.from(this.karaokeConfigs.entries()),
      defaultKaraokeConfigId: this.defaultKaraokeConfig?.id || null,
    });
  }

  /**
   * Set loading state for karaoke generation
   *
   * @param isLoading - Whether karaoke is loading
   */
  setIsKaraokeLoading(isLoading: boolean) {
    this.isKaraokeLoading = isLoading;
  }

  /**
   * Reset the karaoke store state
   */
  reset() {
    this.karaokeLines = [];
    this.isKaraokeGenerated = false;
    this.isKaraokeLoading = false;
  }

  // Getters

  /**
   * Get all karaoke lines
   */
  getKaraokeLinesFormatted(): KaraokeLine[] {
    return this.karaokeLines.map(line => ({
      ...line,
      transcriptTokens: addSpaceToEndOfTranscriptElements(
        fillInWhitespacesInTranscriptElements(
          line.transcriptTokens.filter(token => token.value !== '\n'),
        ),
      ), // fix: need space to insert line break correctly in KaraokeLinesView, TODO: proper fix
    }));
  }

  getKaraokeData(): KaraokeData | null {
    if (!this.karaokeLines.length && !this.loadedKaraokeData) return null;

    if (this.karaokeLines.length > 0) {
      return {
        clips: this.karaokeLines.map(line => ({
          ...line,
          transcriptTokenIds: line.transcriptTokens.map(token => token.id),
        })),
        configs: this.karaokeConfigs,
        defaultConfigId: this.defaultKaraokeConfig?.id || null,
      };
    }

    return this.loadedKaraokeData;
  }

  /**
   * Get a specific karaoke line by ID
   *
   * @param lineId - The ID of the line to retrieve
   */
  getKaraokeLineById(lineId: string): KaraokeLine | undefined {
    return this.karaokeLines.find(line => line.id === lineId);
  }

  /**
   * Get the total number of karaoke lines
   */
  get lineCount(): number {
    return this.karaokeLines.length;
  }

  /**
   * Check if karaoke is ready (generated and not loading)
   */
  get isKaraokeReady(): boolean {
    return this.isKaraokeGenerated && !this.isKaraokeLoading;
  }

  /**
   * Check if there are any karaoke lines available
   */
  get hasKaraokeLines(): boolean {
    return this.karaokeLines.length > 0;
  }

  // Private setters

  private setKaraokeLines(lines: KaraokeLine[]) {
    this.karaokeLines = lines;
  }

  private setIsKaraokeGenerated(isGenerated: boolean) {
    this.isKaraokeGenerated = isGenerated;
  }
}
